/**
 * Utility that provides an easy usage for Gen2Migration smart contract.
 * To prevent users from adding wrong arguments, this is a typescript file.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module gen2-migration.js
 * @class Gen2Migration
 * @since 0.0.1
*/

const Tx = require('ethereumjs-tx').Transaction,
    utils = require('itsa-utils'),
    errorMessages = require('../constants/error-messages'),
    energiTx = require('../helpers/energi-tx'),
    fileToText = require('../helpers/file-to-text'),
    splitArrayIntoBatches = require('../helpers/split-array-into-batches'),
    getKeysFromMnemonic = require('../helpers/mnemonic-parser').getKeysFromMnemonic,
    network = require('../constants/network-info'),
    migrationGas = 0,
    gasLimit = 500000,
    DELAY = 30, // ms
    MAX_BATCH_SEARCH_GEN2 = 1000, // not overload, if beyond then we will split into multiple batches
    EMPTY_TRAILING_ADDRESSES = 100; // according to the specs, it should be 20 in order to assume there are no more. We keep it safe to 100

let keyParser = require('../helpers/key-parser'), // returns {parse (Function)};
    ecsign = require('../helpers/ecsign'),
    networkAddress = network.networkAddress,
    smartContractAddress = network.smartContractAddress,
    abi = network.abi,
    networkId = network.networkId,
    chainId = network.chainId,
    web3;

/**
 * Claims a Gen2 private key and transfers the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claim
 * @param privateKeyGen2 {String} Gen2 private key, not hexadecimal
 * @param addressGen3 {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @param [energiCommon] {Object} leave empty: only for internal usagae to spedd up multiple claims at once
 * @param [directResponse=false] {Boolean} whether to direct response with an Object {tx: {Promise}} or to response with the tx-Promise (default)
 *        Only to be set true internally, to enable simultanious requests by `claimMultiple``
 * @param [hash] {String} precalculated hash: leave empty: only for internal usagae to spedd up multiple claims at once
 * @return {Promise || Object} resolves with tx, or {tx: {Promise}} where tx = smart contract response -> the claim is succeeded if response.status===true
 * @since 0.0.1
 */
// @param [energiCommon] {object} not part of public api -> optional energiCommon config, in case already available, so that `claim` doesn't need to ask from the blockchain
const claim = async (privateKeyGen2, addressGen3, energiCommon, directResponse, hash) => {
    let txCount, parsed, gen2Coins, gen2Coin, signature, smartcontract, transactionData, rawTx, tx,
        serializedTx, privateKey, accountTX, privateKeyTX, txResponse;

    // create a dummy account, just for signing the migration tx, see #2509
    accountTX = web3.eth.accounts.create();
    privateKeyTX = accountTX.privateKey.substr(2);

    if (!hash) {
        hash = await hashToSign(addressGen3);
    }
    parsed = keyParser.parsePrivateKey(privateKeyGen2);

    gen2Coins = await safeSearchRawGen2Coins([parsed.rawOwner], false);
    signature = await ecsign.sign(hash, parsed.key.getPrivate('hex'));

    if (!energiCommon) {
        energiCommon = await energiTx.getCommon(web3, networkAddress, networkId, chainId);
    }

    gen2Coin = gen2Coins[0];
    if (!gen2Coin) {
        throw new ClaimException(errorMessages.NO_GEN2_BALANCE);
    }

    txCount = 0; // new address

    smartcontract = new web3.eth.Contract(abi, smartContractAddress);
    transactionData = smartcontract.methods.claim(gen2Coin.itemId.toString(), addressGen3, ...signature).encodeABI();

    // Create transaction object:
    rawTx = {
        nonce: web3.utils.toHex(txCount),
        value: web3.utils.toHex('0'),
        gasPrice: web3.utils.toHex(migrationGas),
        gasLimit: web3.utils.toHex(gasLimit),
        to: smartContractAddress,
        data: transactionData
    };

    // we NEED an extra check, to be sure that rawTx.to is a valid ethereum address, otherwise the tx-funds will be lost:
    if (!rawTx.to || !web3.utils.isAddress(rawTx.to)) {
        throw new ClaimException(errorMessages.INVALID_SMARTCONTRACT_ADDRESS);
    }

    tx = new Tx(rawTx, {common: energiCommon});

    // Sign the transaction with the GEN3 private key:
    privateKey = Buffer.from(privateKeyTX, 'hex');
    tx.sign(privateKey);

    // Broadcast the transaction to the testnet/mainnet
    serializedTx = tx.serialize();

    if (directResponse) {
        return {
            tx: web3.eth.sendSignedTransaction('0x' + serializedTx.toString('hex'))
        };
    }
    txResponse = await web3.eth.sendSignedTransaction('0x' + serializedTx.toString('hex'));

    if (!txResponse.status) {
        throw new ClaimException(txResponse);
    }
    return txResponse;
};

/**
 * Generates an Exception thrown by a claim error
 *
 * @method ClaimException
 * @protected
 * @param txResponse {Object} the txResponse that raised the exception
 * @since 0.0.1
 */
const ClaimException = function(txResponse) {
    // note: do not use arrow function, because we need to maintain the right context
    let keys, l, i, key;
    this.name = 'ClaimException';
    if (typeof txResponse==='string') {
        this.message = txResponse;
    }
    else {
        this.message = errorMessages.FAILED_TO_CLAIM_GEN2;
        keys = Object.keys(txResponse);
        l = keys.length;
        i = -1;
        while (++i < l) {
            key = keys[i];
            this[key] = txResponse[key];
        }
    }
};

/**
 * Claims multiple Gen2 private keys and transfers the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claimMultiple
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @param addressGen3 {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @return {Promise} Promise holds an array, at each position the smart contract response -> each claim is succeeded if response.status===true, else response.message holds the error.
 * @since 0.0.1
 */
const claimMultiple = async (multiPrivateKeysGen2, addressGen3) => {
    if (!Array.isArray(multiPrivateKeysGen2)) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_REQUIRES_ARRAY);
    }
    if (multiPrivateKeysGen2.length===0) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_EMPTY_ARRAY);
    }
    let txResponses = [],
        claims, hash;
    const total = multiPrivateKeysGen2.length;

    try {
        hash = await hashToSign(addressGen3);
    }
    catch (err) {
        hash = null;
    }
    // Be careful not to overload the browser's javascript runtime, because parsePrivateKey takes some resources.
    // Therefore we introduced a small delay, so that the browser stays responsive
    const claimOne = async (index, multiPrivateKeysGen2, addressGen3, energiCommon) => {
        let txResponse;
        if (index>0) {
            await utils.delay(DELAY);
        }
        try {
            txResponse = await claim(multiPrivateKeysGen2[index], addressGen3, energiCommon, true, hash);
        }
        catch (err) {
            txResponse = {
                tx: Promise.resolve({
                    status: false,
                    message: err.message
                })
            };
        }
        txResponses.push(txResponse);
        index++;
        // If all keys have been processed, then return with `txResponses`,
        // otherwise return with a new promise wich will process the next key:
        return (index<total) ? claimOne(index, multiPrivateKeysGen2, addressGen3, energiCommon) : txResponses;
    };

    const energiCommon = await energiTx.getCommon(web3, networkAddress, networkId, chainId);

    claims = await claimOne(0, multiPrivateKeysGen2, addressGen3, energiCommon);
    // note that `claims` is an array with objects like {tx: Promise}
    // we will return an array with promises, and wait for all items to be resolved
    claims = claims.map(c => c.tx);
    return await Promise.all(claims);
};

/**
 * Claims a Gen2 wallet dump and transfers all the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claimWalletDump
 * @param fileBlob {Blob} Fileblob, that comes from an html input[type="file"] Element: listen for the onChange event and retrieve inputnode.files[0] to get the fileBlob.
 * @param addressGen3 {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @return {Promise} Promise hold an array, at each position the smart contract response -> each claim is succeeded if response.status===true
 * @since 0.0.1
 */
const claimWalletDump = async (fileBlob, addressGen3) => {
    const lines = await fileToText.convertToArray(fileBlob),
        multiPrivateKeysGen2 = await keyParser.parseGen2Dump(lines);
    return claimMultiple(multiPrivateKeysGen2, addressGen3);
};

/**
 * Checks multiple Gen2 private keys for their balance that can be claimed.
 * Will return a Promise which resolves with on Object like this:
 *
 * {
 *     totalAmount,
 *     balancedKeys
 * }
 *
 * Where totalAmount is the sum of the balance that can be claimed by the given privateGen2Keys
 * and balancedKeys is a subset of multiPrivateKeysGen2: only those keys that have a balance.
 *
 * @method getBalancedCoins
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @return {Promise} Promise that resolves with an Object with 2 properties:<br>
 *                   totalAmount (String) total amount that can be claimed
 *                   balancedKeys (Array) a subset of multiPrivateKeysGen2: only those keys that have a balance
 * @since 0.0.1
 */
const getBalancedCoins = async multiPrivateKeysGen2 => {
    let balancedKeys = [];
    const multiPublicKeysGen2 = multiPrivateKeysGen2.map(privateKeyGen2 => keyParser.parsePrivateKey(privateKeyGen2).rawOwner),
        coins = await safeSearchRawGen2Coins(multiPublicKeysGen2, false),
        totalAmount = coins.reduce((sum, coin) => sum.add(coin.amount), web3.utils.toBN(0));

    // note that coins is ordered differently from multiPublicKeysGen2 when searchRawGen2Coins is called woth `true` as second argument -> it is ordered by ItemID
    // that is why we need to find the matching index to retrieve the right private key
    coins.forEach(coin => {
        const index = multiPublicKeysGen2.findIndex(pk => pk===coin.rawOwner);
        balancedKeys.push(multiPrivateKeysGen2[index]);
    });

    return {
        totalAmount: totalAmount.toString(),
        balancedKeys
    };
};

/**
 * Checks multiple Gen2 private keys for their balance that can be claimed.
 * Will return a Promise which resolves with on Object like this:
 *
 * {
 *     totalAmount,
 *     balancedKeys
 * }
 *
 * Where totalAmount is the sum of the balance that can be claimed by the given privateGen2Keys
 * and balancedKeys is a subset of multiPrivateKeysGen2: only those keys that have a balance.
 *
 * @method getBalancedCoinsByMnemonic
 * @param mnemonic {String} a Mnenomic Phrase of 24 words, divided by whitespaces
 * @param [extraPhrase] {String} an optional extra (password) phrase
 * @param [purpose=44] {Number} first number of the BIP44 specification: likely to be 44
 * @param [coinType=204] {Number} second number of the BIP44 specification: coin type
 * @param [account=0] {Number} wallet id that belongs with the mnemonic
 * @param [keyPoolSize=1000] {Number} wallet id that belongs with the mnemonic
 * @param options {Object} config object to made this method behave differently during test phase. ONLY to be used during testing!
 * @return {Promise} Promise that resolves with an Object with 2 properties:<br>
 *                   totalAmount (String) total amount that can be claimed
 *                   balancedKeys (Array) a subset of multiPrivateKeysGen2: only those keys that have a balance
 * @since 0.4.0
 */
const getBalancedCoinsByMnemonic = async (mnemonic, extraPhrase, purpose=44, coinType=204, account=0, keyPoolSize=1000, options) => {
    let balancedKeys = [],
        totalAmount = web3.utils.toBN(0),
        treshold = options && options.treshold,
        replacementPrivateKeys = options && options.replacementPrivateKeys,
        emptyReplacementKey = options && options.emptyReplacementKey,
        balanceInfoExternal, balanceInfoInternal;

    const getBalanceInfo = async (balanceInfo, mnemonic, extraPhrase, purpose, coinType, account, external, skip, keyPoolSize, treshold) => {
        let emptyTrailingAddresses = 0,
            coins, multiPublicKeysGen2, handled, maxIndex,
            keys = await getKeysFromMnemonic(mnemonic, extraPhrase, purpose, coinType, account, external, skip, keyPoolSize);
        // during test phase, we need Gen2 Private keys that have balance, which we cannot get through the menomic phrase
        // so we need to replace those:
        if (replacementPrivateKeys) {
            handled = {};
            keys = keys.map(key => {
                let modifiedKey;
                modifiedKey = {
                    privateKey: (replacementPrivateKeys[key.privateKey] && !handled[key.privateKey]) ? replacementPrivateKeys[key.privateKey] : emptyReplacementKey, // dummy empty balanced PK
                    publicKey: key.publicKey
                };
                handled[key.privateKey] = true;
                return modifiedKey;
            });
        }

        multiPublicKeysGen2 = keys.map(keyPair => keyParser.parsePrivateKey(keyPair.privateKey).rawOwner);

        coins = await safeSearchRawGen2Coins(multiPublicKeysGen2, false);

        // note that coins is ordered differently from multiPublicKeysGen2 when searchRawGen2Coins is called woth `true` as second argument -> it is ordered by ItemID
        // that is why we need to find the matching index to retrieve the right private key
        maxIndex = -1;
        coins.forEach(coin => {
            const index = multiPublicKeysGen2.findIndex(pk => pk===coin.rawOwner);
            const keyPair = keys[index];
            balanceInfo.push({
                privateKey: keyPair.privateKey,
                amount: coin.amount
            });
            if (index>maxIndex) {
                maxIndex = index;
            }
        });
        // EMPTY_TRAILING_ADDRESSES tells how many addresses at the end
        emptyTrailingAddresses = keys.length - maxIndex - 1;
        return (emptyTrailingAddresses>=(treshold || Math.min(EMPTY_TRAILING_ADDRESSES, keyPoolSize))) ?
            balanceInfo :
            getBalanceInfo(balanceInfo, mnemonic, extraPhrase, purpose, coinType, account, external, skip+keyPoolSize, keyPoolSize, treshold);
    };

    try {
        balanceInfoExternal = await getBalanceInfo([], mnemonic, extraPhrase, purpose, coinType, account, true, 0, keyPoolSize, treshold);
        balanceInfoInternal = await getBalanceInfo([], mnemonic, extraPhrase, purpose, coinType, account, false, 0, keyPoolSize, treshold);

        [balanceInfoExternal, balanceInfoInternal].forEach(balanceInfoInternalOrExternal => {
            balanceInfoInternalOrExternal.forEach(balanceInfo => {
                balancedKeys.push(balanceInfo.privateKey);
                totalAmount = totalAmount.add(balanceInfo.amount);
            });
        });
    }
    catch (err) {
        console.error(err);
    }

    return {
        totalAmount: totalAmount.toString(),
        balancedKeys
    };
};

/**
 * Retrieves the balance that can be claimed for a single Gen2 private keys.
 *
 * @method getCoinBalance
 * @param privateKeysGen2 {String} Gen2 private key
 * @return {Promise} Promise which resolves with the balance that can be claimed
 * @since 0.2.0
 */
const getCoinBalance = async privateKeysGen2 => {
    const balancedCoins = await getBalancedCoins([privateKeysGen2]);
    return balancedCoins.totalAmount;
};

/**
 * Generates a hash out of a public Gen3 address.
 * The hash is generated by the smart contract.
 * Note that this function will generate hashToSign both from the network as locally
 * and compare both to prevent anyone to steal coins during migration: https://tasks.energi.software/issues/2972
 *
 * @method hashToSign
 * @param addressGen3 {String} array containing simple types
 * @return {Promise} resolves with a hash, including '0x'
 * @since 0.0.1
 */
const hashToSign = async (addressGen3, forcedLocalHash) => {
    const smartcontract = new web3.eth.Contract(abi, smartContractAddress),
        assertHash = forcedLocalHash || web3.utils.soliditySha3(
            addressGen3,
            '||Energi Gen 2 migration claim||',
            web3.utils.toBN(chainId)
        ),
        hashToSignRemote = await smartcontract.methods.hashToSign(addressGen3).call();

    if (assertHash !== hashToSignRemote) {
        throw new Error();// no need to add a error message: it errors only of someone is trying to steal migration coins
    }
    return hashToSignRemote;
};

/**
 * Will preform web3.energi.searchRawGen2Coins, but at a safe maximum batch size of 1000 addresses.
 * If the amount of owners exceeds, then the routing will split the network request into separate requests
 * and combine the result into one single array if completed
 *
 * @protected
 * @method safeSearchRawGen2Coins
 * @param owners [array] Raw owner addresses
 * @param [includeEmpty] {Boolean}whther to return empty balanced addresses
 * @return {Array} the result of web3.energi.searchRawGen2Coins, but now in a safe way
 * @since 0.7.5
 */
const safeSearchRawGen2Coins = async (owners, includeEmpty) => {
    const batches = splitArrayIntoBatches.split(owners, MAX_BATCH_SEARCH_GEN2);
    let batch, gen2Coins, batchResults = [];
    try {
        for (batch of batches) {
            if (batchResults.length > 0) {
                await utils.delay(DELAY);
            }
            gen2Coins = await web3.energi.searchRawGen2Coins(batch, includeEmpty);
            Array.prototype.push.apply(batchResults, gen2Coins);
        }
    }
    catch (err) {
        batchResults = [];
    }
    return batchResults;
};

/**
 * Verifies if a Gen2 private key has NRG balance that can be claimed for the Gen3 networkAddress and retrieves the balance that can be claimed.
 *
 * @method verifyClaim
 * @param privateKeyGen2 {String} Gen2 private key, not hexadecimal
 * @param [addressGen3] {String} Gen3 public key, may be prepended with '0x', but not required
 * @param [hash] {String} precalculated hash: leave empty: only for internal usagae to spedd up multiple claims at once
 * @return {String} amount of balance that can be claimed
 * @since 0.0.1
 */
const verifyClaim = async (privateKeyGen2, addressGen3, hash) => {
    let newAccount, smartcontract, gen2Coins, signature, gen2Coin;
    const parsed = keyParser.parsePrivateKey(privateKeyGen2);

    if (!addressGen3) {
        newAccount = web3.eth.accounts.create();
        addressGen3 = newAccount.address;
    }
    if (!hash) {
        hash = await hashToSign(addressGen3);
    }
    gen2Coins = await safeSearchRawGen2Coins([parsed.rawOwner], false);
    gen2Coin = gen2Coins[0];
    if (!gen2Coin) {
        console.warn('Error claiming', errorMessages.NO_GEN2_BALANCE);
        return '0';
    }
    signature = ecsign.sign(hash, parsed.key.getPrivate('hex'));
    smartcontract = new web3.eth.Contract(abi, smartContractAddress);
    return smartcontract.methods.verifyClaim(gen2Coin.itemId.toString(), addressGen3, ...signature).call();
};

/**
 * VerifyClaims multiple Gen2 private keys retrieves the balance that can be claimed.
 * A callback function can be used to track the progresss: it will be invoked after every single coin verification with the completed-percentage as only argument.
 *
 * @method verifyClaimMultiple
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @param [addressGen3] {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @param [cb] {Function} callback function that recieves 1 argument: `percentage`. Will be invoked everytime a Gen2 private key is claimed.
 * @return {Promise} Promise hold the total amount (String) that can be claimed
 * @since 0.0.1
 */
const verifyClaimMultiple = async (multiPrivateKeysGen2, addressGen3, cb) => {
    // Be careful not to overload the browser's javascript runtime, because parsePrivateKey takes some resources.
    // Therefore we introduced a small delay, so that the browser stays responsive
    if (typeof addressGen3==='function') {
        // second argument was a cb function
        cb = addressGen3;
        addressGen3 = null;
    }
    // ask for energiCommon object only once, so it can be reused for every single call for `claim`:
    let totalAmount = web3.utils.toBN(0),
        hash;

    try {
        hash = await hashToSign(addressGen3);
    }
    catch (err) {
        hash = null;
    }

    const verifyClaimOne = async (index, multiPrivateKeysGen2, addressGen3) => {
        let percentage;
        const total = multiPrivateKeysGen2.length;
        const amount = await verifyClaim(multiPrivateKeysGen2[index], addressGen3, hash);

        await utils.delay(DELAY);
        totalAmount = totalAmount.add(web3.utils.toBN(amount));
        index++;
        if (typeof cb==='function') {
            percentage = Math.round(100*index/total);
            // now invoke the callback function, to update the caller how much is processed:
            cb(percentage);
        }
        // If all keys have been processed, then return with `txResponses`,
        // otherwise return with a new promise wich will process the next key:
        return (index<total) ? verifyClaimOne(index, multiPrivateKeysGen2, addressGen3) : totalAmount.toString();
    };
    if (!Array.isArray(multiPrivateKeysGen2)) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_REQUIRES_ARRAY);
    }
    if (multiPrivateKeysGen2.length===0) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_EMPTY_ARRAY);
    }
    return verifyClaimOne(0, multiPrivateKeysGen2, addressGen3);
};

/**
 * VerifyClaims a Gen2 wallet dump and retrieves the balance that can be claimed.
 *
 * @method verifyClaimWalletDump
 * @param fileBlob {Blob} Fileblob, that comes from an html input[type="file"] Element: listen for the onChange event and retrieve inputnode.files[0] to get the fileBlob.
 * @param [addressGen3] {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @param [cb] {Function} callback function that recieves 1 argument: `percentage`. Will be invoked everytime a Gen2 private key is claimed.
 * @return {Promise} Promise hold the total amount (String) that can be claimed
 * @since 0.0.1
 */
const verifyClaimWalletDump = async (fileBlob, addressGen3, cb) => {
    const lines = await fileToText.convertToArray(fileBlob),
        multiPrivateKeysGen2 = await keyParser.parseGen2Dump(lines);
    return verifyClaimMultiple(multiPrivateKeysGen2, addressGen3, cb);
};

module.exports = (web3Instance, options) => {
    if (options) {
        abi = options.abi || abi;
        keyParser = options.keyParser || keyParser;
        networkAddress = options.networkAddress || networkAddress;
        smartContractAddress = options.smartContractAddress || smartContractAddress;
        networkId = options.networkId || networkId;
        chainId = options.chainId || chainId;
        ecsign = options.ecsign || ecsign;
    }
    // check first if we have a valid web3 instance:
    if (web3Instance && web3Instance.utils && web3Instance.utils.unitMap && (web3Instance.utils.unitMap.wei==='1')) {
        web3 = web3Instance;
        return {
            claim,
            claimMultiple,
            claimWalletDump,
            getBalancedCoins,
            getBalancedCoinsByMnemonic,
            getCoinBalance,
            hashToSign,
            verifyClaim,
            verifyClaimMultiple,
            verifyClaimWalletDump
        };
    }
    console.error(errorMessages.INITIALIZE_WITH_WEB3);
};
